import { __decorate } from "tslib";
import isNil from 'lodash/isNil';
import { action, observable, runInAction } from 'mobx';
import { warning } from '../utils';
import DataSet from '../data-set/DataSet';
import axios from '../axios';
import { FieldType } from '../data-set/enum';
import { LovFieldType } from '../enum';
import { getGlobalConfig, processAxiosConfig } from './utils';
import { mergeDataSetProps } from '../data-set/utils';
function getFieldType(conditionFieldType) {
    switch (conditionFieldType) {
        case LovFieldType.INT:
            return FieldType.number;
        case LovFieldType.TEXT:
            return FieldType.string;
        case LovFieldType.DATE:
            return FieldType.date;
        case LovFieldType.DATETIME:
            return FieldType.dateTime;
        case LovFieldType.POPUP:
            return FieldType.object;
        default:
            return conditionFieldType || FieldType.string;
    }
}
function generateConditionField(fields, { conditionField, conditionFieldType, conditionFieldName, gridFieldName, display, conditionFieldLovCode, conditionFieldSelectCode, conditionFieldSelectUrl, conditionFieldSelectTf, conditionFieldSelectVf, conditionFieldRequired, fieldProps, }) {
    if (conditionField === 'Y') {
        const name = conditionFieldName || gridFieldName;
        const field = {
            name,
            type: getFieldType(conditionFieldType),
            label: display,
            lovCode: conditionFieldLovCode || undefined,
            lookupCode: conditionFieldSelectCode || undefined,
            lookupUrl: conditionFieldSelectUrl || undefined,
            textField: conditionFieldSelectTf || undefined,
            valueField: conditionFieldSelectVf || undefined,
            required: conditionFieldRequired || undefined,
            ...fieldProps,
        };
        fields.push(field);
        if (conditionFieldType === LovFieldType.POPUP) {
            const aliasName = `__lov__${name}`;
            field.name = aliasName;
            fields.push({
                name,
                bind: `${aliasName}.${conditionFieldSelectVf}`,
            });
        }
    }
}
function generateGridField(fields, { gridField, gridFieldName, display, fieldProps }) {
    if (gridField === 'Y') {
        fields.push({
            name: gridFieldName,
            label: display,
            ...fieldProps,
        });
    }
}
export class LovCodeStore {
    constructor() {
        this.pendings = {};
        this.init();
    }
    getAxios(field) {
        return getGlobalConfig('axios', field) || axios;
    }
    init() {
        this.lovCodes = observable.map();
    }
    getDefineAxiosConfig(code, field, record) {
        const lovDefineAxiosConfig = (field && field.get('lovDefineAxiosConfig', record)) || getGlobalConfig('lovDefineAxiosConfig', field);
        const config = processAxiosConfig(lovDefineAxiosConfig, code, field);
        return {
            ...config,
            url: config.url || this.getConfigUrl(code, field, record),
            method: config.method || 'post',
        };
    }
    getConfig(code) {
        return this.lovCodes.get(code);
    }
    async fetchConfig(code, field, record) {
        let config = this.getConfig(code);
        // SSR do not fetch the lookup
        if (!config && typeof window !== 'undefined') {
            const axiosConfig = this.getDefineAxiosConfig(code, field, record);
            if (axiosConfig) {
                try {
                    const pending = this.pendings[code] || this.getAxios(field)(axiosConfig);
                    this.pendings[code] = pending;
                    config = await pending;
                    runInAction(() => {
                        if (config) {
                            this.lovCodes.set(code, config);
                        }
                    });
                }
                finally {
                    delete this.pendings[code];
                }
            }
        }
        return config;
    }
    // lovCode 作为key 缓存了 ds
    getLovDataSet(code, field, dataSetProps, record) {
        const config = this.getConfig(code);
        if (config) {
            const { lovPageSize, lovItems, parentIdField, idField, treeFlag, dataSetProps: configDataSetProps } = config;
            const valueField = field ? field.get('valueField', record) : config.valueField;
            const dsProps = {
                transport: {
                    read: this.getQueryAxiosConfig(code, field, config, record),
                },
                primaryKey: valueField,
                cacheSelection: true,
            };
            if (!isNil(lovPageSize) && !isNaN(Number(lovPageSize))) {
                dsProps.pageSize = Number(lovPageSize);
            }
            else {
                dsProps.paging = false;
            }
            if (treeFlag === 'Y' && parentIdField && idField) {
                dsProps.parentField = parentIdField;
                dsProps.idField = idField;
            }
            if (lovItems && lovItems.length) {
                const { querys, fields } = lovItems
                    .sort(({ conditionFieldSequence }, { conditionFieldSequence: conditionFieldSequence2 }) => conditionFieldSequence - conditionFieldSequence2)
                    .reduce((obj, configItem) => {
                    generateConditionField(obj.querys, configItem);
                    generateGridField(obj.fields, configItem);
                    return obj;
                }, { querys: [], fields: [] });
                if (querys.length) {
                    dsProps.queryFields = querys;
                }
                if (fields.length) {
                    dsProps.fields = fields;
                }
            }
            return new DataSet(mergeDataSetProps(mergeDataSetProps(dsProps, configDataSetProps), dataSetProps));
        }
        warning(false, `LOV: code<${code}> is not exists`);
        return undefined;
    }
    getConfigUrl(code, field, record) {
        const lovDefineUrl = (field && field.get('lovDefineUrl', record)) || getGlobalConfig('lovDefineUrl', field);
        if (typeof lovDefineUrl === 'function') {
            return lovDefineUrl(code);
        }
        return lovDefineUrl;
    }
    getQueryAxiosConfig(code, field, config, record) {
        return (props) => {
            const lovQueryAxiosConfig = (field && field.get('lovQueryAxiosConfig', record)) || getGlobalConfig('lovQueryAxiosConfig', field);
            const lovQueryUrl = this.getQueryUrl(code, field, props, record);
            const axiosConfig = processAxiosConfig(lovQueryAxiosConfig, code, config, props, lovQueryUrl);
            return {
                ...axiosConfig,
                url: axiosConfig.url || lovQueryUrl,
                method: axiosConfig.method || 'post',
            };
        };
    }
    getQueryUrl(code, field, props, record) {
        const config = this.getConfig(code);
        if (config) {
            const { customUrl } = config;
            if (customUrl) {
                return customUrl;
            }
        }
        const lovQueryUrl = (field && field.get('lovQueryUrl', record)) || getGlobalConfig('lovQueryUrl', field);
        if (typeof lovQueryUrl === 'function') {
            return lovQueryUrl(code, config, props);
        }
        return lovQueryUrl;
    }
    clearCache(codes) {
        if (codes) {
            codes.forEach(code => {
                this.lovCodes.delete(code);
            });
        }
        else {
            this.lovCodes.clear();
        }
    }
}
__decorate([
    observable
], LovCodeStore.prototype, "lovCodes", void 0);
__decorate([
    action
], LovCodeStore.prototype, "init", null);
__decorate([
    action
], LovCodeStore.prototype, "clearCache", null);
export default new LovCodeStore();
